import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { RecurringBillingConsent } from '@/components/billing/RecurringBillingConsent';
import { GrossPrice, PricingBreakdown } from '@/components/billing/PricingBreakdown';
import { isPayablePricingDisplayContract, type PricingDisplayContract } from '@/lib/billing/pricingDisplay';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface CheckoutJourneyTabsProps {
  translationScope: 'hire.checkout' | 'seek.checkout';
  brand: 'hire' | 'seek';
  platform: 'forkhire' | 'forkseek';
  planId?: string | null;
  planName: string;
  billingCycle: 'monthly' | 'yearly';
  introPrice?: number | null;
  renewalPrice: number | null;
  introPricing?: PricingDisplayContract | null;
  renewalPricing?: PricingDisplayContract | null;
  priceAvailable: boolean;
  pricingLoading?: boolean;
  pricingError?: boolean;
  billingConsent?: boolean;
  onBillingConsentChange: (checked: boolean) => void;
  disabled?: boolean;
  confirmDisabled?: boolean;
  isProcessing?: boolean;
  confirmLabel: string;
  onConfirm: () => void;
}

export function CheckoutJourneyTabs({
  translationScope,
  brand,
  platform,
  planName,
  billingCycle,
  introPrice,
  renewalPrice,
  introPricing,
  renewalPricing,
  priceAvailable,
  pricingLoading = false,
  pricingError = false,
  onBillingConsentChange,
  disabled = false,
  confirmDisabled = false,
  isProcessing = false,
  confirmLabel,
  onConfirm,
}: CheckoutJourneyTabsProps) {
  const { t } = useTranslation();
  const duePricing = introPricing ?? renewalPricing;
  const pricingReady = !pricingLoading && !pricingError &&
    isPayablePricingDisplayContract(duePricing) &&
    isPayablePricingDisplayContract(renewalPricing);
  const accentText = brand === 'seek' ? 'text-seek' : 'text-primary';
  const accentBg = brand === 'seek' ? 'bg-seek' : 'bg-primary';

  const handleConfirm = () => {
    if (!priceAvailable || !pricingReady || disabled || confirmDisabled) return;
    onBillingConsentChange(true);
    onConfirm();
  };

  return (
    <Card className="rounded-xl border-border/80 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg">{t(`${translationScope}.journeyTitle`)}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">{planName}</p>
          </div>
          <div className={cn('inline-flex h-10 w-10 items-center justify-center rounded-xl text-primary-foreground', accentBg)}>
            <CheckCircle2 className="h-5 w-5" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-xl border border-border bg-muted/25 p-4 text-sm">
          <div className="flex items-center gap-2">
            <ShieldCheck className={cn('h-4 w-4', accentText)} />
            <p className="font-semibold text-foreground">{t(`${translationScope}.upgradeSummary`)}</p>
          </div>
          <div className="mt-3 grid gap-2 text-muted-foreground sm:grid-cols-2">
            <div className="rounded-lg bg-background/70 p-3">
              <span className="block text-xs">{t(`${translationScope}.plan`)}</span>
              <span className="font-semibold text-foreground">{planName}</span>
            </div>
            <div className="rounded-lg bg-background/70 p-3">
              <span className="block text-xs">{t(`${translationScope}.billing`)}</span>
              <span className="font-semibold text-foreground">
                {billingCycle === 'yearly' ? t(`${translationScope}.yearly`) : t(`${translationScope}.monthly`)}
              </span>
            </div>
            {introPrice != null && renewalPrice != null && introPrice !== renewalPrice && (
              <div className="rounded-lg bg-background/70 p-3">
                <span className="block text-xs">{t(`${translationScope}.renewal`)}</span>
                {renewalPricing ? <GrossPrice pricing={renewalPricing} size="small" suffix={<>/{billingCycle === 'yearly' ? t(`${translationScope}.yearAbbr`) : t(`${translationScope}.monthAbbr`)}</>} /> : null}
              </div>
            )}
            <div className="rounded-lg bg-background/70 p-3">
              <span className="block text-xs">{t(`${translationScope}.totalDueToday`)}</span>
              {duePricing ? <GrossPrice pricing={duePricing} size="small" /> : t(`${translationScope}.pricingUnavailable`)}
            </div>
          </div>
          {duePricing ? <PricingBreakdown pricing={duePricing} className="mt-3 bg-background/70" /> : null}
        </div>

        {priceAvailable && pricingReady ? (
          <RecurringBillingConsent
            planName={planName}
            billingCycle={billingCycle}
            introPricing={introPricing}
            renewalPricing={renewalPricing}
            platform={platform}
          />
        ) : (
          <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-xs text-destructive">
            {pricingLoading
              ? t(`${translationScope}.pricingLoading`, { defaultValue: 'Loading authoritative pricing…' })
              : t(`${translationScope}.pricingConsentUnavailable`)}
          </div>
        )}

        <Button
          className={cn('h-11 w-full rounded-xl', brand === 'seek' && 'bg-seek text-white hover:bg-seek/90')}
          disabled={!priceAvailable || !pricingReady || disabled || confirmDisabled}
          onClick={handleConfirm}
        >
          {isProcessing ? <><Loader2 className="me-1.5 h-4 w-4 animate-spin" />{t('common.processing')}</> : confirmLabel}
        </Button>
        <p className={cn('text-center text-[11px] font-medium', accentText)}>
          {t(`${translationScope}.journeyAuthorizationNote`)}
        </p>
      </CardContent>
    </Card>
  );
}
